import React, { useMemo, useEffect } from "react";
import "./top10.css";
import TimeStamp from "../Dashboard/components/time-stamp/TimeStamp";
import RankCard from "../../Components/rank-card/RankCard";
import DashButton from "../Dashboard/components/dash-button/dashButton";
import useTop10 from "./use-Top10";
import Top10Item from "./Top10Item/Top10Item";
import DesignationDropdown from "../../Components/DesignationDropdown/DesignationDropdown";
import Feedback from "../../Components/feedback/Feedback";
import CircularProgress from "@mui/material/CircularProgress";
import useAgentProfile from "../AgentProfile/use-AgentProfile"; 
import CookiesServices from "../../services/CookiesService";

const Top10 = () => {
  const {
    data,
    timeframe,
    setTimeframe,
    selectedCategory,
    setSelectedCategory,
    isMobile,
    getPlayers,
    getRank,
    categories: baseCategories,
    designationLevels,
    selectedLevel,
    setSelectedLevel,
    fetchLeaderboard,
    loading,
    error,
    feedback,
    setFeedback,
  } = useTop10();

  const { formData: agentProfile, photoUrl, photoLoading, loading: profileLoading,} = useAgentProfile();

  // Column order: National → Zone → Region → others
  const categories = useMemo(() => {
    const order = ["national", "zone", "region"];
    return [
      ...baseCategories.filter((cat) => order.includes(cat.key)).sort(
        (a, b) => order.indexOf(a.key) - order.indexOf(b.key)
      ),
      ...baseCategories.filter((cat) => !order.includes(cat.key)),
    ];
  }, [baseCategories]);

  // Filter only categories that have players
  const visibleCategories = useMemo(() => {
    return categories.filter(
      (cat) => (getPlayers(cat.key) || []).length > 0
    );
  }, [categories, getPlayers]);

  // Auto-select first available category (mobile)
  useEffect(() => {
    if (isMobile && visibleCategories.length > 0) {
      const isValid = visibleCategories.some(
        (cat) => cat.key === selectedCategory
      );

      if (!isValid) {
        setSelectedCategory(visibleCategories[0].key);
      }
    }
  }, [visibleCategories, isMobile]);

  if (loading || profileLoading) {
    return (
      <div className="summary-wrapper">
        <CircularProgress size={60} />
      </div>
    );
  }

  const cookiesServices = new CookiesServices();
  const hasProfile = String(cookiesServices.get("hasProfile")).toLowerCase() === "true";
  const isProfileMissing = !agentProfile;
  const isPhotoMissing = agentProfile && !agentProfile.profilePhoto;
  const isBlocked = hasProfile && (isProfileMissing );

  return (
    <div className="top10-container">

      {/* Warning Overlay*/}
      {isBlocked && (
        <div className="leaderboard-overlay">
          <Feedback
            type="warning"
            message={
              isProfileMissing
                ? "Please complete your agent profile to access the ANBP leaderboard."
                : "Please upload profile photo to access the ANBP leaderboard."
            }
            onClose={() => {}}
          />
        </div>
      )}

      {feedback && (
        <Feedback {...feedback} onClose={() => setFeedback(null)} />
      )}

      {/* Blur Content*/}
      <div className={`leaderboard-content ${isBlocked ? "blurred" : ""}`}>

        <div className="top10-header">
          <div className="designation-dropdown-container">
            <DesignationDropdown
              levels={designationLevels}
              selectedLevel={selectedLevel}
              onSelect={(level) => {
                setSelectedLevel(level);
                fetchLeaderboard(timeframe, level);
              }}
            />
          </div>
          <div className="dash-new-button-container">
            <DashButton
              label="MONTH TO DATE"
              onClick={() => setTimeframe("mtd")}
              variant={timeframe === "mtd" ? "primary" : "secondary"}
            />
            <DashButton
              label="YEAR TO DATE"
              onClick={() => setTimeframe("ytd")}
              variant={timeframe === "ytd" ? "primary" : "secondary"}
            />
          </div>
        </div>

        {!isMobile && (
          <div className="top10-columns">
            {visibleCategories.map((cat) => {
              const players = getPlayers(cat.key) || [];

              return (
                <div className="top10-column fade-in" key={cat.key}>
                  <div className="column-rank-card">
                    <RankCard
                      title={data?.[cat.key]?.title || cat.label}
                      name={data?.[cat.key]?.name}
                      rank={getRank(cat.key) === -1 ? "-" : getRank(cat.key)}
                      icon={cat.icon}
                      color={cat.color}
                    />
                  </div>

                  {players
                    .sort((a, b) => b.anbp - a.anbp || b.nop - a.nop)
                    .map((player, index) => (
                      <Top10Item
                        key={player.agentCode}
                        player={player}
                        index={index}
                        timeframe={timeframe}
                      />
                    ))}
                </div>
              );
            })}
          </div>
        )}

        {isMobile && (
          <>
            <div className="mobile-rankcard-row">
              {visibleCategories.map((cat) => (
                <div
                  key={cat.key}
                  className={`mobile-rankcard ${selectedCategory === cat.key ? "active" : ""

                  }`}
                  onClick={() => setSelectedCategory(cat.key)}
                >
                  <RankCard
                    title={data?.[cat.key]?.title || cat.label}
                    name={data?.[cat.key]?.name}
                    rank={getRank(cat.key) === -1 ? "-" : getRank(cat.key)}
                    icon={cat.icon}
                    color={cat.color}
                  />
                </div>
              ))}
            </div>
            <div className="mobile-rankers-list">
              {[...(getPlayers(selectedCategory) || [])]
                .sort((a, b) => b.anbp - a.anbp || b.nop - a.nop)
                .map((player, index) => (
                  <Top10Item
                    key={player.agentCode}
                    player={player}
                    index={index}
                    timeframe={timeframe}
                  />
                ))}
            </div>

          </>
        )}

        {/* Show message if ALL empty */}
        {visibleCategories.length === 0 && (
          <div className="no-data">No Agents Available</div>
        )}

        {data?.timestamp && (
          <div className="timestamp">
            <TimeStamp timestamp={data.timestamp} />
          </div>
        )}
      </div>

      {error && (
        <div className="summary-wrapper">
          <Feedback
            type="error"
            message="Failed to load leaderboard data. Please try again later."
            onClose={() => setFeedback(null)}
          />
        </div>
      )}
    </div>
  );
};

export default Top10;
